/**
 * EspectroAccountManager -- orchestrates PKP lifecycle, key hierarchy, and
 * Zcash enter/exit/balance operations with SwapKit and lightwalletd
 * integration. All state is persisted to localStorage (no backend).
 *
 * Operation state is tracked via the durable EspectroOperation model
 * (see operation.ts) which survives page refreshes and browser crashes.
 */

import { base64 } from "@scure/base";
import {
  localGenerateZcashSeed,
  localDeriveZcashSeedFromSignature,
  localCreateShieldingTx,
  localCreateUnshieldingTx,
  localCreateTransparentTx,
  localGetShieldedBalance,
  type LocalBalanceResult as BalanceResult,
  DERIVATION_MESSAGE,
} from "../local/actions.ts";
import {
  createLightwalletClient,
  type LightwalletClient,
  type UTXO,
} from "../zcash/lightwalletd.ts";
import {
  SwapKitClient,
  ZEC_ASSET,
  txAsEVM,
  trackChainId,
  isCompleted,
  type TrackResponse,
} from "../swapkit/index.ts";
import {
  type EspectroOperation,
  type EnterEvidence,
  type ExitEvidence,
  type TransferEvidence,
  type TransferStage,
  createEnterOperation,
  createExitOperation,
  createTransferOperation,
  advanceOperation,
  failOperation,
  completeOperation,
  saveOperation,
  loadOperation,
  clearOperation,
  isTerminalStage,
  hasChainCommitment,
} from "./operation.ts";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "espectro:account";
const ZATS_PER_ZEC = 1e8;


/** ZIP 317 fee: max(2, logical_actions) * 5000 zats.
 *  logical_actions = max(t_in, t_out) + sapling + orchard.
 *  Orchard builder pads to >=2 actions. */
function zip317Fee(numTransparentInputs: number, numTransparentOutputs: number, numOrchardActions = 2): number {
  const logicalActions = Math.max(numTransparentInputs, numTransparentOutputs) + numOrchardActions;
  return Math.max(2, logicalActions) * 5000;
}


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EspectroAccount {
  seedB64: string;
  transparentAddress: string;
  unifiedAddress: string;
  unifiedAddressEnter: string;
  unifiedAddressExit: string;
  birthdayHeight?: number;
  lastScannedHeight?: number;
  cachedBalance?: string;
  cachedNoteCount?: number;
  cachedNotes?: Array<{ value: number; note_commitment: string; nullifier: string; recipient: string; rho: string; rseed: string; position?: number; auth_path?: string[] }>;
}

export type EnterStep =
  | "quoting"
  | "signing"
  | "swapping"
  | "waiting_zec"
  | "shielding"
  | "done";

export type ExitStep =
  | "scanning"
  | "unshielding"
  | "waiting_confirm"
  | "quoting"
  | "swapping"
  | "done";

export interface SyncProgress {
  phase: "checking" | "downloading" | "scanning" | "done" | "cached";
  startHeight?: number;
  endHeight?: number;
  blocksToScan?: number;
}

export interface EnterResult {
  swapTxHash?: string;
  shieldingTxId: string;
  shieldedAmountZats: number;
}

export interface ExitResult {
  unshieldingTxId: string;
  swapTxHash?: string;
  spentAmountZats: number;
}

export interface EVMTransactionRequest {
  to: string;
  from: string;
  gas: string;
  gasPrice: string;
  value: string;
  data: string;
}

export interface EnterParams {
  sourceAsset: string;
  amount: string;
  sourceAddress: string;
  destinationAddress: string;
  preBalanceZats?: number;
  signAndSend?: (tx: EVMTransactionRequest) => Promise<string>;
  onProgress?: (step: EnterStep) => void;
}

export interface ExitParams {
  destinationAsset: string;
  destinationAddress: string;
  amount: string;
  preBalanceZats?: number;
  onProgress?: (step: ExitStep) => void;
}

export interface TransferParams {
  sourceAsset: string;
  sourceAddress: string;
  amount: string;
  destinationAsset: string;
  destinationAddress: string;
  signAndSend: (tx: EVMTransactionRequest) => Promise<string>;
  /** Sign an arbitrary message with the connected ETH wallet for deterministic ZEC seed derivation. */
  signMessage?: (message: string) => Promise<string>;
  onProgress?: (stage: TransferStage) => void;
}

export interface TransferResult {
  enterSwapTxHash?: string;
  shieldingTxId: string;
  unshieldingTxId: string;
  exitSwapTxHash?: string;
}

async function withRetry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3,
  baseDelayMs = 2000,
): Promise<T> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === maxAttempts - 1) throw err;
      const delay = baseDelayMs * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw new Error("unreachable");
}

// ---------------------------------------------------------------------------
// Account persistence
// ---------------------------------------------------------------------------

function saveAccount(account: EspectroAccount): void {
  // Never persist the seed — keep it in memory only to prevent XSS exfiltration.
  // The seed is re-derived from the ETH wallet signature on every session.
  const { seedB64: _seed, ...toSave } = account;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
}

function clearAccount(): void {
  localStorage.removeItem(STORAGE_KEY);
  clearOperation();
}

// ---------------------------------------------------------------------------
// Manager
// ---------------------------------------------------------------------------

export class EspectroAccountManager {
  private account: EspectroAccount | null = null;
  private lwClient: LightwalletClient;
  private swapClient: SwapKitClient;
  private _confirmCheckInFlight = false;

  constructor() {
    this.lwClient = createLightwalletClient();
    this.swapClient = new SwapKitClient({ debug: true });
  }

  // ---- Lifecycle ----------------------------------------------------------

  loadAccount(): EspectroAccount | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as EspectroAccount;
      // seedB64 is intentionally not persisted — absence is expected on page load.
      if (!parsed.transparentAddress) return null;
      parsed.unifiedAddressEnter ??= parsed.unifiedAddress;
      parsed.unifiedAddressExit ??= parsed.unifiedAddress;
      delete (parsed as any).cachedAnchor;
      delete (parsed as any).cachedFrontier;
      this.account = parsed;
      return parsed;
    } catch {
      clearAccount();
      return null;
    }
  }

  async createAccount(ethSignature?: string): Promise<EspectroAccount> {
    console.log("[espectro] createAccount: deriving ZEC seed...");

    const seedResult = ethSignature
      ? await localDeriveZcashSeedFromSignature(ethSignature)
      : await localGenerateZcashSeed();
    console.log("[espectro] Zcash seed derived:", seedResult.transparentAddress);

    let birthdayHeight: number | undefined;
    try {
      birthdayHeight = await this.lwClient.getLatestBlockHeight();
      console.log("[espectro] Birthday height (current):", birthdayHeight);
    } catch (err) {
      console.warn("[espectro] Could not fetch birthday height:", err);
    }

    // If re-deriving an existing account mid-operation (e.g. hard refresh while a
    // transfer is in progress), preserve the earlier birthday so the scan window
    // covers the shielded note. For fresh starts (no active operation with chain
    // commitment) always use the current block — stale birthdays from prior
    // sessions would cause unnecessarily long scans.
    const existing = this.loadAccount();
    const activeOp = loadOperation();
    const hasActiveCommitment = activeOp != null && !isTerminalStage(activeOp.stage) && hasChainCommitment(activeOp);
    if (
      hasActiveCommitment &&
      existing &&
      existing.transparentAddress === seedResult.transparentAddress &&
      existing.birthdayHeight != null &&
      (birthdayHeight == null || existing.birthdayHeight < birthdayHeight)
    ) {
      console.log(`[espectro] Preserving stored birthdayHeight ${existing.birthdayHeight} (current block: ${birthdayHeight})`);
      birthdayHeight = existing.birthdayHeight;
    }

    const account: EspectroAccount = {
      seedB64: seedResult.seedB64,
      transparentAddress: seedResult.transparentAddress,
      unifiedAddress: seedResult.unifiedAddress,
      unifiedAddressEnter: seedResult.unifiedAddressEnter,
      unifiedAddressExit: seedResult.unifiedAddressExit,
      birthdayHeight,
      // Preserve lastScannedHeight only if it doesn't skip blocks before birthdayHeight.
      // If it's ahead of birthdayHeight (corrupted by prior re-derivations), reset it.
      lastScannedHeight: (
        existing?.transparentAddress === seedResult.transparentAddress &&
        existing.lastScannedHeight != null &&
        birthdayHeight != null &&
        existing.lastScannedHeight <= birthdayHeight
      ) ? existing.lastScannedHeight : birthdayHeight,
    };

    this.account = account;
    saveAccount(account);
    return account;
  }

  async logout(): Promise<void> {
    this.account = null;
    clearAccount();
  }

  getAccount(): EspectroAccount | null {
    return this.account;
  }

  // ---- Operation access ---------------------------------------------------

  getOperation(): EspectroOperation | null {
    return loadOperation();
  }

  // ---- Enter (source asset -> shielded ZEC) -------------------------------

  async enter(params: EnterParams): Promise<EnterResult> {
    const acct = this.requireAccount();
    const progress = params.onProgress ?? (() => {});

    // Snapshot existing UTXOs for attribution
    let knownUtxosBefore: Array<{ txid: string; vout: number }> = [];
    try {
      const existing = await this.lwClient.getUTXOs(acct.transparentAddress);
      knownUtxosBefore = existing.map((u) => ({ txid: u.txid, vout: u.vout }));
    } catch {
      // Non-fatal: we proceed without a snapshot
    }

    // Step 1: Get swap quote (source asset -> ZEC)
    progress("quoting");
    const quoteResp = await this.swapClient.quote({
      sellAsset: params.sourceAsset,
      buyAsset: ZEC_ASSET,
      sellAmount: params.amount,
      slippage: 3,
      sourceAddress: params.sourceAddress,
      destinationAddress: acct.transparentAddress,
    });

    if (!quoteResp.routes.length) {
      throw new Error(quoteResp.error ?? "No swap routes available");
    }

    const route = quoteResp.routes[0]!;

    // Step 2: Execute swap — create the durable operation record
    progress("signing");
    console.log("[espectro] enter swap request:", {
      routeId: route.routeId,
      sourceAddress: params.sourceAddress,
      destinationAddress: acct.transparentAddress,
    });
    const swapResp = await this.swapClient.swap({
      routeId: route.routeId,
      sourceAddress: params.sourceAddress,
      destinationAddress: acct.transparentAddress,
      disableBalanceCheck: true,
      disableEstimate: true,
      disableSecurityChecks: true,
    });
    console.log("[espectro] enter swap response:", {
      swapId: swapResp.swapId,
      error: swapResp.error,
      hasTx: !!swapResp.tx,
      txFrom: (swapResp.tx as Record<string, unknown>)?.from,
      txTo: (swapResp.tx as Record<string, unknown>)?.to,
      txValue: (swapResp.tx as Record<string, unknown>)?.value,
      txGas: (swapResp.tx as Record<string, unknown>)?.gas,
      txGasPrice: (swapResp.tx as Record<string, unknown>)?.gasPrice,
    });

    if (swapResp.error) {
      throw new Error(swapResp.error);
    }

    let op = createEnterOperation({
      sourceAsset: params.sourceAsset,
      sourceAddress: params.sourceAddress,
      amount: params.amount,
      preBalanceZats: params.preBalanceZats ?? 0,
    });
    op = advanceOperation(op, "swap_created", {
      swapId: swapResp.swapId,
      knownUtxosBefore,
    } as Partial<EnterEvidence>);

    let swapTxHash: string | undefined;
    try {
      if (swapResp.tx && params.signAndSend) {
        const evmTx = txAsEVM(swapResp);
        op = advanceOperation(op, "swap_created", {
          evmTx: evmTx as unknown as Record<string, unknown>,
        } as Partial<EnterEvidence>);
        swapTxHash = await params.signAndSend(evmTx);
        op = advanceOperation(op, "source_broadcasted", {
          swapTxHash,
        } as Partial<EnterEvidence>);
      } else if (swapResp.tx) {
        const evmTx = txAsEVM(swapResp);
        op = advanceOperation(op, "swap_created", {
          evmTx: evmTx as unknown as Record<string, unknown>,
        } as Partial<EnterEvidence>);
      }
    } catch (err) {
      failOperation(op, err instanceof Error ? err.message : "Signing failed");
      throw err;
    }

    progress("swapping");
    swapTxHash ??= swapResp.swapId;

    // Step 3: Poll for ZEC arrival at transparent address
    progress("waiting_zec");
    let utxos: UTXO[];
    try {
      utxos = await this.lwClient.pollForDeposit(acct.transparentAddress, {
        intervalMs: 15_000,
        timeoutMs: 600_000,
        excludeUtxos: knownUtxosBefore,
      });

      await this.verifyInboundSwap(op, swapTxHash ?? swapResp.swapId, trackChainId(params.sourceAsset));

      op = advanceOperation(op, "inbound_detected", {
        depositUtxos: utxos.map((u) => ({
          txid: u.txid,
          vout: u.vout,
          amount: u.amount,
          script: u.script,
          height: u.height,
        })),
      } as Partial<EnterEvidence>);
    } catch (err) {
      failOperation(
        op,
        err instanceof Error ? err.message : "Deposit polling failed",
      );
      throw err;
    }

    // Step 4: Shield ZEC into Orchard pool (minus ZIP 317 fee)
    progress("shielding");
    const totalZats = utxos.reduce(
      (sum, u) => sum + Math.round(parseFloat(u.amount) * ZATS_PER_ZEC),
      0,
    );
    const fee = zip317Fee(utxos.length, 0);
    const shieldAmountZats = totalZats - fee;
    if (shieldAmountZats <= 0) {
      const msg = `UTXO value (${totalZats} zats) too small to cover fee (${fee} zats)`;
      failOperation(op, msg);
      throw new Error(msg);
    }

    let shieldResult: { txid: string; raw_tx?: string };
    try {
      shieldResult = await localCreateShieldingTx({
        seedB64: acct.seedB64,
        utxos: JSON.stringify(utxos),
        amount: shieldAmountZats.toString(),
        provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
      });
    } catch (err) {
      failOperation(
        op,
        err instanceof Error ? err.message : "Shielding tx build failed",
      );
      throw err;
    }

    op = advanceOperation(op, "shielding_built", {
      shieldRawTx: shieldResult.raw_tx,
      shieldTxId: shieldResult.txid,
      shieldedAmountZats: shieldAmountZats,
    } as Partial<EnterEvidence>);

    // Step 5: Broadcast
    if (shieldResult.raw_tx) {
      try {
        const realShieldTxid = await this.lwClient.sendRawTransaction(shieldResult.raw_tx);
        console.log("[espectro] shield tx on-chain:", realShieldTxid);
        op = advanceOperation(
          op,
          "shielding_broadcasted",
        );
      } catch (err) {
        // Leave at shielding_built so recovery can retry broadcast
        op.error = err instanceof Error ? err.message : "Broadcast failed";
        saveOperation(op);
        throw err;
      }
    }

    this.invalidateScanCache();

    // Operation stays at shielding_broadcasted — the balance poll in the
    // dashboard will advance to shield_confirmed -> completed once the
    // shielded note appears on chain.
    progress("done");

    return {
      swapTxHash,
      shieldingTxId: shieldResult.txid,
      shieldedAmountZats: shieldAmountZats,
    };
  }

  // ---- Exit (shielded ZEC -> destination asset) ---------------------------
  //
  // Privacy optimization: we get the SwapKit quote FIRST to discover their
  // ZEC deposit address, then unshield directly to that address (z->t).
  // The MPC t-addr never appears on the exit side.

  async exit(params: ExitParams): Promise<ExitResult> {
    const acct = this.requireAccount();
    const progress = params.onProgress ?? (() => {});

    let op = createExitOperation({
      destinationAsset: params.destinationAsset,
      destinationAddress: params.destinationAddress,
      amount: params.amount,
      preBalanceZats: params.preBalanceZats ?? 0,
    });

    // Step 1: Fresh single-pass scan with tree tracking for anchor + witnesses.
    progress("scanning");
    let witnessResult: BalanceResult;
    try {
      witnessResult = await this.prepareExitWitnesses((p) => {
        if (p.phase === "scanning" || p.phase === "downloading")
          progress("scanning");
      });
    } catch (err) {
      failOperation(
        op,
        err instanceof Error ? err.message : "Witness scan failed",
      );
      throw err;
    }

    if (
      parseInt(witnessResult.balance) === 0 ||
      !witnessResult.notes?.length
    ) {
      const msg = `No shielded balance to exit (balance=${witnessResult.balance}, notes=${witnessResult.notes?.length ?? 0})`;
      failOperation(op, msg);
      throw new Error(msg);
    }
    if (!witnessResult.anchor) {
      const msg =
        "Witness scan did not produce an anchor — tree state unavailable";
      failOperation(op, msg);
      throw new Error(msg);
    }

    // Step 2: Get swap quote to discover SwapKit's ZEC deposit address
    const unshieldFee = zip317Fee(0, 1, 2);
    const requestedZats = Math.round(Number(params.amount) * ZATS_PER_ZEC);
    const netZats = requestedZats - unshieldFee;
    if (netZats <= 0) {
      const msg = `Amount (${requestedZats} zats) too small to cover unshielding fee (${unshieldFee} zats)`;
      failOperation(op, msg);
      throw new Error(msg);
    }
    const netZec = (netZats / ZATS_PER_ZEC).toFixed(8);

    progress("quoting");
    let swapResp: Awaited<ReturnType<SwapKitClient["swap"]>>;
    try {
      const quoteResp = await this.swapClient.quote({
        sellAsset: ZEC_ASSET,
        buyAsset: params.destinationAsset,
        sellAmount: netZec,
        slippage: 3,
        sourceAddress: acct.transparentAddress,
        destinationAddress: params.destinationAddress,
      });

      if (!quoteResp.routes.length) {
        throw new Error(quoteResp.error ?? "No swap routes for exit");
      }

      const route = quoteResp.routes[0]!;
      swapResp = await this.swapClient.swap({
        routeId: route.routeId,
        sourceAddress: acct.transparentAddress,
        destinationAddress: params.destinationAddress,
        disableBuildTx: true,
        disableBalanceCheck: true,
        disableEstimate: true,
        disableSecurityChecks: true,
      });

      if (swapResp.error) {
        throw new Error(swapResp.error);
      }
    } catch (err) {
      failOperation(
        op,
        err instanceof Error ? err.message : "Exit quote/swap failed",
      );
      throw err;
    }

    const unshieldTarget = swapResp.inboundAddress ?? acct.transparentAddress;
    op = advanceOperation(op, "exit_quote_ready", {
      swapId: swapResp.swapId,
      unshieldTarget,
      requestedAmountZats: requestedZats,
      netZats,
    } as Partial<ExitEvidence>);

    // Step 3: Unshield using the fresh witness data
    progress("unshielding");
    let unshieldResult: { txid: string; raw_tx?: string };
    try {
      unshieldResult = await localCreateUnshieldingTx({
        seedB64: acct.seedB64,
        notes: JSON.stringify(witnessResult.notes),
        amount: netZats.toString(),
        destinationTAddr: unshieldTarget,
        provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
        anchor: witnessResult.anchor,
        merklePaths: JSON.stringify(
          witnessResult.notes!.map((n) => ({
            position: n.position,
            auth_path: n.auth_path,
          })),
        ),
      });
    } catch (err) {
      failOperation(
        op,
        err instanceof Error ? err.message : "Unshielding tx build failed",
      );
      throw err;
    }

    op = advanceOperation(op, "unshield_built", {
      unshieldRawTx: unshieldResult.raw_tx,
      unshieldTxId: unshieldResult.txid,
    } as Partial<ExitEvidence>);

    // Step 4: Broadcast unshielding tx
    if (unshieldResult.raw_tx) {
      try {
        const realUnshieldTxid = await this.lwClient.sendRawTransaction(unshieldResult.raw_tx);
        console.log("[espectro] unshield tx on-chain:", realUnshieldTxid);
        op = advanceOperation(op, "unshield_broadcasted");
      } catch (err) {
        op.error = err instanceof Error ? err.message : "Broadcast failed";
        saveOperation(op);
        throw err;
      }
    }

    this.invalidateScanCache();

    // Step 5: Track the outbound swap until terminal
    op = advanceOperation(op, "swap_pending", {
      swapStatus: "pending",
    } as Partial<ExitEvidence>);
    progress("swapping");

    try {
      const trackResult = await this.swapClient.pollTrack(
        { hash: unshieldResult.txid, chainId: "zcash" },
        15_000,
        3_600_000,
      );

      if (isCompleted(trackResult)) {
        console.log(
          `[espectro] exit: swap completed (status=${trackResult.status}, legs=${trackResult.legs?.map((l) => l.status).join(" -> ") ?? "none"})`,
        );
        op = advanceOperation(op, "swap_completed", {
          swapStatus: "completed",
        } as Partial<ExitEvidence>);
        completeOperation(op);
      } else {
        console.log(
          `[espectro] exit: swap terminal but not completed (status=${trackResult.status})`,
        );
        throw new Error(`Swap ${trackResult.status}`);
      }
    } catch (err) {
      // Swap tracking failed/timed out — leave at swap_pending for resume
      op = advanceOperation(op, "swap_pending", {
        swapStatus: "tracking_failed",
      } as Partial<ExitEvidence>);
      op.error = err instanceof Error ? err.message : "Swap tracking failed";
      saveOperation(op);
      throw err;
    }

    progress("done");

    return {
      unshieldingTxId: unshieldResult.txid,
      swapTxHash: swapResp.swapId,
      spentAmountZats: requestedZats,
    };
  }

  // ---- Swap verification ---------------------------------------------------

  /**
   * Check whether the inbound SwapKit swap has failed or been refunded.
   * Throws if the swap reached a negative terminal state; returns silently
   * if completed or still pending (SwapKit tracking can lag behind chain
   * state, so UTXOs appearing is treated as the stronger signal).
   */
  private async verifyInboundSwap(
    op: EspectroOperation,
    txHash: string | undefined,
    chainId: string,
  ): Promise<void> {
    if (!txHash) return;
    try {
      const resp = await this.swapClient.track({ hash: txHash, chainId });
      if (resp.status === "failed" || resp.status === "refunded") {
        failOperation(op, `Source swap ${resp.status}`);
        throw new Error(`Source swap ${resp.status} — aborting enter flow`);
      }
    } catch (err) {
      if (
        err instanceof Error &&
        err.message.startsWith("Source swap ")
      ) {
        throw err;
      }
      console.warn("[espectro] verifyInboundSwap: track call failed (non-fatal):", err);
    }
  }

  // ---- Reconciliation -----------------------------------------------------

  /**
   * Check live chain/swap state and advance the operation if conditions are
   * met. Called on page load and after balance refreshes.
   */
  async reconcileOperation(): Promise<EspectroOperation | null> {
    const op = loadOperation();
    if (!op || isTerminalStage(op.stage)) return op;

    try {
      if (op.type === "enter") {
        return await this.reconcileEnter(op);
      }
      if (op.type === "exit") {
        return await this.reconcileExit(op);
      }
    } catch (err) {
      console.warn("[espectro] reconciliation error:", err);
    }
    return op;
  }

  private async reconcileEnter(op: EspectroOperation): Promise<EspectroOperation> {
    const acct = this.requireAccount();
    const ev = op.evidence as EnterEvidence;

    if (
      op.stage === "swap_created" ||
      op.stage === "source_broadcasted"
    ) {
      try {
        await this.verifyInboundSwap(op, ev.swapTxHash ?? ev.swapId, trackChainId(ev.sourceAsset ?? ""));
      } catch {
        return op;
      }

      // Check if ZEC arrived at the transparent address
      try {
        const utxos = await this.lwClient.getUTXOs(acct.transparentAddress);
        const knownSet = new Set(
          (ev.knownUtxosBefore ?? []).map((u) => `${u.txid}:${u.vout}`),
        );
        const newUtxos = utxos.filter(
          (u) => !knownSet.has(`${u.txid}:${u.vout}`),
        );
        if (newUtxos.length > 0) {
          return advanceOperation(op, "inbound_detected", {
            depositUtxos: newUtxos.map((u) => ({
              txid: u.txid,
              vout: u.vout,
              amount: u.amount,
              script: u.script,
              height: u.height,
            })),
          } as Partial<EnterEvidence>);
        }
      } catch {
        // Non-fatal
      }
    }

    if (op.stage === "shielding_broadcasted") {
      const confirmed = await this.checkShieldConfirmation(op);
      return confirmed ?? op;
    }

    return op;
  }

  private async reconcileExit(op: EspectroOperation): Promise<EspectroOperation> {
    const ev = op.evidence as ExitEvidence;

    if (
      (op.stage === "unshield_broadcasted" || op.stage === "swap_pending") &&
      (ev.unshieldTxId || ev.swapId)
    ) {
      try {
        const resp = await this.swapClient.track({ hash: ev.unshieldTxId ?? ev.swapId!, chainId: "zcash" });
        if (isCompleted(resp)) {
          op = advanceOperation(op, "swap_completed", {
            swapStatus: "completed",
          } as Partial<ExitEvidence>);
          return completeOperation(op);
        }
        if (resp.status === "refunded" || resp.status === "failed") {
          return failOperation(op, `Swap ${resp.status}`);
        }
        advanceOperation(op, "swap_pending", {
          swapStatus: resp.status,
        } as Partial<ExitEvidence>);
      } catch {
        // Non-fatal
      }
    }

    return op;
  }

  /**
   * Check if the shielded balance has reached the expected post-shield level,
   * confirming that the shielding tx landed on chain.
   *
   * Guarded against concurrent invocations to prevent stacking expensive
   * balance scans during the 15s poll interval.
   */
  async checkShieldConfirmation(
    op?: EspectroOperation | null,
  ): Promise<EspectroOperation | null> {
    op ??= loadOperation();
    if (!op || op.type !== "enter" || op.stage !== "shielding_broadcasted") {
      return op ?? null;
    }
    if (this._confirmCheckInFlight) return op;

    this._confirmCheckInFlight = true;
    try {
      const ev = op.evidence as EnterEvidence;
      const preBalance = ev.preBalanceZats ?? 0;
      const shieldedAmount = ev.shieldedAmountZats ?? 0;
      const expectedBalance = preBalance + shieldedAmount;
      const TOLERANCE_ZATS = 10_000; // 0.0001 ZEC for fee rounding

      const balResult = await this.getShieldedBalance();
      const currentBalance = parseInt(balResult.balance);
      if (currentBalance >= expectedBalance - TOLERANCE_ZATS) {
        op = advanceOperation(op, "shield_confirmed");
        return completeOperation(op);
      }
    } catch {
      // Non-fatal
    } finally {
      this._confirmCheckInFlight = false;
    }

    return op;
  }

  /**
   * Resume swap tracking for an exit operation stuck at swap_pending.
   */
  async resumeExitSwapTracking(
    onStatus?: (status: string) => void,
  ): Promise<EspectroOperation | null> {
    const op = loadOperation();
    if (!op || op.type !== "exit") return op ?? null;
    if (op.stage !== "swap_pending" && op.stage !== "unshield_broadcasted") {
      return op;
    }

    const ev = op.evidence as ExitEvidence;
    if (!ev.unshieldTxId && !ev.swapId) return op;

    const report = onStatus ?? (() => {});
    report("tracking");

    try {
      const trackResult = await this.swapClient.pollTrack(
        { hash: ev.unshieldTxId ?? ev.swapId!, chainId: "zcash" },
        15_000,
        3_600_000,
      );
      if (isCompleted(trackResult)) {
        advanceOperation(op, "swap_completed", {
          swapStatus: "completed",
        } as Partial<ExitEvidence>);
        report("completed");
        return completeOperation(op);
      }
      report(trackResult.status);
      return failOperation(op, `Swap ${trackResult.status}`);
    } catch (err) {
      report("error");
      op.error = err instanceof Error ? err.message : "Swap tracking failed";
      saveOperation(op);
      return op;
    }
  }

  // ---- Transfer (single-use obfuscation pipeline) -------------------------

  async transfer(params: TransferParams): Promise<TransferResult> {
    const progress = params.onProgress ?? (() => {});

    let op = loadOperation();
    const isResume = op?.type === "transfer" && !isTerminalStage(op.stage);

    // Ensure the seed is in memory — always re-derive from wallet signature.
    // The seed is never persisted to localStorage, so this is required on every
    // fresh page load (including resumes after refresh).
    const ensureSeed = async () => {
      if (this.account?.seedB64) return; // already in memory
      if (!params.signMessage) {
        throw new Error("Wallet signature required to derive Zcash keys.");
      }
      let ethSig: string;
      try {
        ethSig = await params.signMessage(DERIVATION_MESSAGE);
      } catch {
        throw new Error("Wallet signature declined. Please approve the signature request to continue.");
      }
      await withRetry(() => this.createAccount(ethSig));
    };

    if (!isResume) {
      // --- INITIATING: create ephemeral PKP + derive keys ---
      progress("initiating");
      await ensureSeed();
      op = createTransferOperation({
        sourceAsset: params.sourceAsset,
        sourceAddress: params.sourceAddress,
        amount: params.amount,
        destinationAsset: params.destinationAsset,
        destinationAddress: params.destinationAddress,
      });
      saveOperation(op);
      op = advanceOperation(op, "swap_quoting");
    } else {
      op = op!;
      if (op.error) {
        console.log(`[espectro] Resuming transfer at ${op.stage} (retry ${op.retryCount}, previous error: ${op.error})`);
        op.error = undefined;
        saveOperation(op);
      }
      // Re-derive seed into memory if this is a resume after page refresh.
      if (!this.account) this.loadAccount();
      await ensureSeed();
    }

    const acct = this.requireAccount();
    const ev = () => op!.evidence as TransferEvidence;

    try {
      // --- SWAP QUOTING ---
      if (op.stage === "swap_quoting") {
        progress("swap_quoting");

        let knownUtxosBefore: Array<{ txid: string; vout: number }> = [];
        try {
          const existing = await this.lwClient.getUTXOs(acct.transparentAddress);
          knownUtxosBefore = existing.map((u) => ({ txid: u.txid, vout: u.vout }));
        } catch { /* non-fatal */ }

        const quoteResp = await withRetry(() =>
          this.swapClient.quote({
            sellAsset: params.sourceAsset,
            buyAsset: ZEC_ASSET,
            sellAmount: params.amount,
            slippage: 3,
            sourceAddress: params.sourceAddress,
            destinationAddress: acct.transparentAddress,
          }),
        );

        if (!quoteResp.routes.length) {
          throw new Error(quoteResp.error ?? "No swap routes available");
        }

        const route = quoteResp.routes[0]!;
        console.log("[espectro] transfer swap request:", {
          routeId: route.routeId,
          sourceAddress: params.sourceAddress,
          destinationAddress: acct.transparentAddress,
        });
        const swapResp = await this.swapClient.swap({
          routeId: route.routeId,
          sourceAddress: params.sourceAddress,
          destinationAddress: acct.transparentAddress,
          disableBalanceCheck: true,
          disableEstimate: true,
          disableSecurityChecks: true,
        });
        console.log("[espectro] transfer swap response:", {
          swapId: swapResp.swapId,
          error: swapResp.error,
          hasTx: !!swapResp.tx,
          txFrom: (swapResp.tx as Record<string, unknown>)?.from,
          txValue: (swapResp.tx as Record<string, unknown>)?.value,
        });

        if (swapResp.error) {
          throw new Error(swapResp.error);
        }

        op = advanceOperation(op, "swap_signing", {
          enterSwapId: swapResp.swapId,
          knownUtxosBefore,
          approvalTx: swapResp.approvalTx ? (swapResp.approvalTx as unknown as Record<string, unknown>) : undefined,
          evmTx: swapResp.tx ? (txAsEVM(swapResp) as unknown as Record<string, unknown>) : undefined,
        } as Partial<TransferEvidence>);
      }

      // --- SWAP SIGNING (user interaction) ---
      if (op.stage === "swap_signing") {
        progress("swap_signing");

        // For ERC-20 tokens (e.g. USDC), SwapKit returns an approval tx that
        // must be confirmed on-chain before the swap tx can be submitted.
        const approvalTxData = ev().approvalTx;
        if (approvalTxData && params.signAndSend) {
          await params.signAndSend(approvalTxData as unknown as EVMTransactionRequest);
        }

        const evmTxData = ev().evmTx;
        if (evmTxData && params.signAndSend) {
          const evmTx = evmTxData as unknown as EVMTransactionRequest;
          const txHash = await params.signAndSend(evmTx);
          op = advanceOperation(op, "swap_confirming", {
            enterSwapTxHash: txHash,
          } as Partial<TransferEvidence>);
        } else {
          op = advanceOperation(op, "swap_confirming", {
            enterSwapTxHash: ev().enterSwapId,
          } as Partial<TransferEvidence>);
        }
      }

      // --- SWAP CONFIRMING + DEPOSIT ARRIVING ---
      if (op.stage === "swap_confirming") {
        progress("swap_confirming");
        op = advanceOperation(op, "deposit_arriving");
      }

      if (op.stage === "deposit_arriving") {
        progress("deposit_arriving");

        const utxos = await this.lwClient.pollForDeposit(acct.transparentAddress, {
          intervalMs: 15_000,
          timeoutMs: 600_000,
          excludeUtxos: ev().knownUtxosBefore,
        });

        if (ev().enterSwapTxHash || ev().enterSwapId) {
          await this.verifyInboundSwap(
            op,
            ev().enterSwapTxHash ?? ev().enterSwapId,
            trackChainId(params.sourceAsset),
          );
        }

        op = advanceOperation(op, "shielding", {
          depositUtxos: utxos.map((u) => ({
            txid: u.txid,
            vout: u.vout,
            amount: u.amount,
            script: u.script,
            height: u.height,
          })),
        } as Partial<TransferEvidence>);
      }

      // --- SHIELDING ---
      if (op.stage === "shielding") {
        progress("shielding");

        const rawUtxos = ev().depositUtxos;
        if (!rawUtxos?.length) {
          throw new Error("No deposit UTXOs recorded for shielding");
        }
        // Deduplicate by txid:vout to prevent bad-txns-inputs-duplicate
        const seen = new Set<string>();
        const utxos = rawUtxos.filter((u) => {
          const key = `${u.txid}:${u.vout}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        const totalZats = utxos.reduce(
          (sum, u) => sum + Math.round(parseFloat(u.amount) * ZATS_PER_ZEC),
          0,
        );
        const fee = zip317Fee(utxos.length, 0);
        const shieldAmountZats = totalZats - fee;
        if (shieldAmountZats <= 0) {
          throw new Error(`UTXO value (${totalZats} zats) too small to cover fee (${fee} zats)`);
        }

        const shieldResult = await withRetry(() =>
          localCreateShieldingTx({
            seedB64: acct.seedB64,
            utxos: JSON.stringify(utxos),
            amount: shieldAmountZats.toString(),
            provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
          }),
        );

        op = advanceOperation(op, "shielding", {
          shieldRawTx: shieldResult.raw_tx,
          shieldTxId: shieldResult.txid,
          shieldedAmountZats: shieldAmountZats,
        } as Partial<TransferEvidence>);

        if (shieldResult.raw_tx) {
          const realShieldTxid = await this.lwClient.sendRawTransaction(shieldResult.raw_tx);
          console.log("[espectro] transfer shield tx on-chain:", realShieldTxid);
          op = advanceOperation(op, "shielding", { shieldTxId: realShieldTxid } as Partial<TransferEvidence>);
        }

        this.invalidateScanCache();
        op = advanceOperation(op, "shield_confirming");
      }

      // --- SHIELD CONFIRMING ---
      if (op.stage === "shield_confirming") {
        progress("shield_confirming");

        const shieldedAmount = ev().shieldedAmountZats ?? 0;
        const TOLERANCE_ZATS = 10_000;
        let confirmed = false;

        for (let attempt = 0; attempt < 40; attempt++) {
          try {
            const balResult = await this.getShieldedBalance();
            const currentBalance = parseInt(balResult.balance);
            if (currentBalance >= shieldedAmount - TOLERANCE_ZATS) {
              confirmed = true;
              break;
            }
          } catch { /* retry */ }
          await new Promise((r) => setTimeout(r, 15_000));
        }

        if (!confirmed) {
          throw new Error("Shield confirmation timed out after 10 minutes");
        }

        op = advanceOperation(op, "exit_preparing");
      }

      // --- EXIT PREPARING (scan + quote) ---
      if (op.stage === "exit_preparing") {
        progress("exit_preparing");

        // Determine the correct birthday so the scan window covers the shielded note.
        // Strategy (in priority order):
        //   1. Look up the confirmed block of the shield tx via Blockchair/Tatum.
        //   2. Fall back to estimating from op.createdAt — the note can't be earlier
        //      than when the operation started, so (now - age) is a safe lower bound.
        // Both paths permanently fix a too-high birthday stored by an early hard refresh.
        const knownShieldTxId = (op.evidence as TransferEvidence).shieldTxId;
        console.log(`[espectro] exit_preparing: shieldTxId=${knownShieldTxId ?? "none"}, birthdayHeight=${acct.birthdayHeight}`);
        const shieldBlockHeight = await this.lwClient.getAddressShieldBlockHeight(acct.transparentAddress, knownShieldTxId).catch((e) => { console.warn("[espectro] exit_preparing: shieldBlockHeight lookup failed:", e); return null; });
        console.log(`[espectro] exit_preparing: shieldBlockHeight=${shieldBlockHeight}`);

        if (shieldBlockHeight && (acct.birthdayHeight == null || shieldBlockHeight <= acct.birthdayHeight)) {
          console.log(`[espectro] exit_preparing: adjusting birthdayHeight ${acct.birthdayHeight} → ${shieldBlockHeight} (shield block)`);
          acct.birthdayHeight = shieldBlockHeight;
          saveAccount(acct);
        } else if (!shieldBlockHeight && op?.createdAt) {
          // If the shield block is unknown, estimate from the operation's creation timestamp.
          // The shield tx must be at a block whose timestamp is >= createdAt, so scanning from
          // (latestBlock - ageBlocks - buffer) guarantees we cover it.
          try {
            const latestBlock = await this.lwClient.getLatestBlockHeight();
            const ageMs = Date.now() - op.createdAt;
            const ageBlocks = Math.floor(ageMs / 75_000);
            const ageBasedBirthday = Math.max(1, latestBlock - ageBlocks - 50);
            if (ageBasedBirthday < (acct.birthdayHeight ?? Infinity)) {
              console.log(`[espectro] exit_preparing: age-based birthday ${ageBasedBirthday} (op age ~${Math.round(ageMs / 60_000)} min ≈ ${ageBlocks} blocks, was ${acct.birthdayHeight})`);
              acct.birthdayHeight = ageBasedBirthday;
              saveAccount(acct);
            }
          } catch (e) {
            console.warn("[espectro] exit_preparing: age-based birthday fallback failed:", e);
          }
        }

        // Clear the scan cache so we rescan from birthdayHeight on every attempt.
        acct.lastScannedHeight = undefined;
        acct.cachedBalance = undefined;
        acct.cachedNoteCount = undefined;
        acct.cachedNotes = undefined;

        let witnessResult: Awaited<ReturnType<typeof this.prepareExitWitnesses>>;
        try {
          witnessResult = await this.prepareExitWitnesses();
        } finally {
          // Always clear lastScannedHeight so the next retry rescans from birthdayHeight.
          acct.lastScannedHeight = undefined;
        }

        if (parseInt(witnessResult.balance) === 0 || !witnessResult.notes?.length) {
          throw new Error("No shielded balance to exit");
        }
        if (!witnessResult.anchor) {
          throw new Error("Witness scan did not produce an anchor");
        }

        const unshieldFee = zip317Fee(0, 1, 2);
        const balanceZats = parseInt(witnessResult.balance);
        const netZats = balanceZats - unshieldFee;
        if (netZats <= 0) {
          throw new Error(`Balance (${balanceZats} zats) too small to cover unshield fee (${unshieldFee} zats)`);
        }
        const netZec = (netZats / ZATS_PER_ZEC).toFixed(8);

        const quoteResp = await withRetry(() =>
          this.swapClient.quote({
            sellAsset: ZEC_ASSET,
            buyAsset: params.destinationAsset,
            sellAmount: netZec,
            slippage: 3,
            sourceAddress: acct.transparentAddress,
            destinationAddress: params.destinationAddress,
          }),
        );

        if (!quoteResp.routes.length) {
          throw new Error(quoteResp.error ?? "No swap routes for exit");
        }

        const route = quoteResp.routes[0]!;
        const swapResp = await this.swapClient.swap({
          routeId: route.routeId,
          sourceAddress: acct.transparentAddress,
          destinationAddress: params.destinationAddress,
          disableBuildTx: true,
          disableBalanceCheck: true,
          disableEstimate: true,
          disableSecurityChecks: true,
        });

      if (swapResp.error) {
        throw new Error(swapResp.error);
      }

      const unshieldTarget = swapResp.inboundAddress ?? acct.transparentAddress;

        // Build and broadcast unshielding tx
        const unshieldResult = await withRetry(() =>
          localCreateUnshieldingTx({
            seedB64: acct.seedB64,
            notes: JSON.stringify(witnessResult.notes),
            amount: netZats.toString(),
            destinationTAddr: unshieldTarget,
            provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
            anchor: witnessResult.anchor,
            merklePaths: JSON.stringify(
              witnessResult.notes!.map((n) => ({
                position: n.position,
                auth_path: n.auth_path,
              })),
            ),
          }),
        );

        op = advanceOperation(op, "unshielding", {
          exitSwapId: swapResp.swapId,
          unshieldTarget,
          unshieldRawTx: unshieldResult.raw_tx,
          unshieldTxId: unshieldResult.txid,
          netZats,
          expectedReceiveAmount: route.expectedBuyAmount,
          expectedReceiveAmountMin: route.expectedBuyAmountMaxSlippage,
          exitSlippageBps: route.totalSlippageBps,
          exitSellAmountZec: netZec,
        } as Partial<TransferEvidence>);
      }

      // --- UNSHIELDING ---
      if (op.stage === "unshielding") {
        progress("unshielding");

        const rawTx = ev().unshieldRawTx;
        if (rawTx) {
          try {
            const realUnshieldTxid = await this.lwClient.sendRawTransaction(rawTx);
            console.log("[espectro] transfer unshield tx on-chain:", realUnshieldTxid);
            op = advanceOperation(op, "unshielding", { unshieldTxId: realUnshieldTxid } as Partial<TransferEvidence>);
          } catch (broadcastErr) {
            const errMsg = broadcastErr instanceof Error ? broadcastErr.message : String(broadcastErr);
            if (
              errMsg.includes("bad-txns-orchard-unknown-anchor") ||
              errMsg.includes("bad-txns-orchard-duplicate-nullifier")
            ) {
              // Stale anchor or duplicate nullifier — reset to exit_preparing so next retry rebuilds with a fresh scan
              console.warn(`[espectro] unshield rejected (${errMsg.includes("anchor") ? "stale anchor" : "duplicate nullifier"}), resetting to exit_preparing for rescan`);
              op = advanceOperation(op, "exit_preparing", {
                unshieldRawTx: undefined,
                unshieldTxId: undefined,
              } as Partial<TransferEvidence>);
              throw new Error(`Unshield rejected, rescan required: ${errMsg}`);
            }
            throw broadcastErr;
          }
        }

        this.invalidateScanCache();
        op = advanceOperation(op, "exit_confirming", {
          exitSwapStatus: "pending",
        } as Partial<TransferEvidence>);
      }

      // --- EXIT CONFIRMING ---
      if (op.stage === "exit_confirming") {
        progress("exit_confirming");

        const exitTrackHash = ev().unshieldTxId ?? ev().exitSwapId;
        if (exitTrackHash) {
          const trackResult = await this.swapClient.pollTrack(
            { hash: exitTrackHash, chainId: "zcash" },
            15_000,
            3_600_000,
          );

          if (!isCompleted(trackResult)) {
            const legInfo = trackResult.legs?.length
              ? `, legs: [${trackResult.legs.map((l) => l.status).join(", ")}]`
              : "";
            throw new Error(`Exit swap ended with status: ${trackResult.status}${legInfo}`);
          }

          op = advanceOperation(op, "exit_confirming", {
            exitSwapStatus: "completed",
          } as Partial<TransferEvidence>);
        }

        completeOperation(op);
        progress("completed");
      }

    } catch (err) {
      if (op && !isTerminalStage(op.stage)) {
        const msg = err instanceof Error ? err.message : "Transfer failed";
        const RETRYABLE_POST_SHIELD_STAGES: ReadonlySet<string> = new Set([
          "shield_confirming", "exit_preparing", "unshielding", "exit_confirming",
        ]);
        const MAX_TRANSFER_RETRIES = 5;

        if (RETRYABLE_POST_SHIELD_STAGES.has(op.stage) && op.retryCount < MAX_TRANSFER_RETRIES) {
          op.retryCount++;
          op.error = msg;
          saveOperation(op);
          console.warn(`[espectro] Transfer failed at ${op.stage} (retry ${op.retryCount}/${MAX_TRANSFER_RETRIES}): ${msg}`);
        } else {
          failOperation(op, msg);
        }
      }
      throw err;
    }

    // Collect results before wiping
    const evidence = ev();
    const result: TransferResult = {
      enterSwapTxHash: evidence.enterSwapTxHash,
      shieldingTxId: evidence.shieldTxId!,
      unshieldingTxId: evidence.unshieldTxId!,
      exitSwapTxHash: evidence.exitSwapId,
    };

    // Do NOT wipe account after transfer — the ZEC seed is deterministic from
    // the ETH signature so it's safe to keep. Wiping here caused a new random
    // seed (and new transparent address) on every subsequent transfer.

    return result;
  }

  /**
   * Wipe all local state (account + operation). Only call after verifying
   * the transfer is complete or no funds are at risk.
   */
  safeWipe(): void {
    const op = loadOperation();
    if (op && hasChainCommitment(op)) {
      console.warn("[espectro] safeWipe blocked: operation has chain commitments");
      return;
    }
    this.account = null;
    clearAccount();
  }

  /**
   * Force wipe all state regardless of chain commitments.
   * Only use when the user explicitly confirms they understand fund loss risk.
   */
  forceWipe(): void {
    this.account = null;
    clearAccount();
  }

  // ---- Balance (display-only, no tree tracking) ---------------------------

  private static SCAN_BATCH_SIZE = 100;

  async getShieldedBalance(
    compactBlocksOverride?: string,
    onProgress?: (progress: SyncProgress) => void,
  ): Promise<BalanceResult> {
    const acct = this.requireAccount();
    const report = onProgress ?? (() => {});

    if (compactBlocksOverride) {
      const result = localGetShieldedBalance({
        seedB64: acct.seedB64,
        compactBlocks: compactBlocksOverride,
      });
      return result;
    }

    report({ phase: "checking" });
    const latestHeight = await this.lwClient.getLatestBlockHeight();

    if (acct.birthdayHeight == null) {
      acct.birthdayHeight = Math.max(1, latestHeight - 1200);
      acct.lastScannedHeight = undefined;
      acct.cachedBalance = undefined;
      acct.cachedNoteCount = undefined;
      acct.cachedNotes = undefined;
      saveAccount(acct);
      console.log(`[espectro] Backfilled birthday height: ${acct.birthdayHeight}`);
    }

    let cursor = acct.lastScannedHeight ?? acct.birthdayHeight;

    const validCachedNotes = Array.isArray(acct.cachedNotes) ? acct.cachedNotes : undefined;
    if (acct.cachedNotes !== undefined && !Array.isArray(acct.cachedNotes)) {
      console.warn("[espectro] cachedNotes corrupted, forcing rescan");
      cursor = acct.birthdayHeight;
      acct.lastScannedHeight = undefined;
      acct.cachedBalance = undefined;
      acct.cachedNoteCount = undefined;
      acct.cachedNotes = undefined;
      saveAccount(acct);
    }

    if (cursor > latestHeight) {
      report({ phase: "cached" });
      return {
        balance: acct.cachedBalance ?? "0",
        noteCount: acct.cachedNoteCount ?? 0,
        latestBlockHeight: latestHeight,
        notes: validCachedNotes ?? [],
      };
    }

    const totalBlocks = latestHeight - cursor + 1;
    const allNotes: Array<{ value: number; note_commitment: string; nullifier: string; recipient: string; rho: string; rseed: string; position?: number; auth_path?: string[] }> = [...(validCachedNotes ?? [])];
    const allSeenNullifiers = new Set<string>();

    console.log(`[espectro] Scanning blocks ${cursor}..${latestHeight} (${totalBlocks} blocks in batches of ${EspectroAccountManager.SCAN_BATCH_SIZE})`);

    while (cursor <= latestHeight) {
      const batchEnd = Math.min(cursor + EspectroAccountManager.SCAN_BATCH_SIZE - 1, latestHeight);
      const batchBlocks = batchEnd - cursor + 1;

      report({
        phase: "downloading",
        startHeight: cursor,
        endHeight: batchEnd,
        blocksToScan: totalBlocks,
      });

      const blockData = await this.lwClient.getCompactBlocks(cursor, batchEnd);
      const compactBlocksB64 = base64.encode(blockData);

      report({
        phase: "scanning",
        startHeight: cursor,
        endHeight: batchEnd,
        blocksToScan: totalBlocks,
      });

      const result = localGetShieldedBalance({
        seedB64: acct.seedB64,
        compactBlocks: compactBlocksB64,
      });

      if (result.noteCount > 0) {
        console.log(`[espectro] Batch result with notes:`, {
          noteCount: result.noteCount,
          balance: result.balance,
          notesLength: result.notes?.length,
        });
      }
      if (result.notes) allNotes.push(...result.notes);
      if (result.seenNullifiers) {
        for (const nf of result.seenNullifiers) allSeenNullifiers.add(nf);
      }

      cursor = batchEnd + 1;
      acct.lastScannedHeight = cursor;
      saveAccount(acct);

      console.log(`[espectro] Scanned batch ${cursor - batchBlocks}..${batchEnd}: found ${result.noteCount} notes (${batchBlocks} blocks), seenNullifiers=${result.seenNullifiers?.length ?? 0}`, (result as any).selfTest ? `selfTest=${(result as any).selfTest}` : '');
    }

    const unspentNotes = allNotes.filter(n => !allSeenNullifiers.has(n.nullifier));
    const spentCount = allNotes.length - unspentNotes.length;
    if (spentCount > 0) {
      console.log(`[espectro] Nullifier filter: ${spentCount} spent notes removed, ${unspentNotes.length} unspent remain`);
    }

    let finalBalance = BigInt(0);
    for (const n of unspentNotes) finalBalance += BigInt(n.value);

    acct.cachedNotes = unspentNotes;
    acct.cachedBalance = finalBalance.toString();
    acct.cachedNoteCount = unspentNotes.length;
    saveAccount(acct);

    report({ phase: "done" });
    return {
      balance: finalBalance.toString(),
      noteCount: unspentNotes.length,
      latestBlockHeight: latestHeight,
      notes: unspentNotes,
    };
  }

  // ---- Exit witness preparation (single-pass with tree tracking) ----------

  private async prepareExitWitnesses(
    onProgress?: (progress: SyncProgress) => void,
  ): Promise<BalanceResult> {
    const acct = this.requireAccount();
    const report = onProgress ?? (() => {});

    report({ phase: "checking" });

    // First, do a balance scan to find what block the shielded note landed in.
    // The note may arrive 2–5 blocks after birthdayHeight, so we need the
    // anchorHeight to be >= the block the note was actually found at, not just
    // >= birthdayHeight + 1.
    const balResult = await this.getShieldedBalance();
    const noteScanLatestHeight = balResult.latestBlockHeight;

    // We need anchorHeight to satisfy two constraints:
    //   1. anchorHeight >= noteScanLatestHeight — so the note block is included
    //   2. anchorHeight <= latestHeight - 3    — so the block is fully indexed
    // Wait until enough blocks have confirmed after shielding to satisfy both.
    const minAnchor = noteScanLatestHeight;
    let latestHeight = await this.lwClient.getLatestBlockHeight();
    while (latestHeight - 3 < minAnchor) {
      console.log(`[espectro] Exit: waiting for blocks to settle (latest=${latestHeight}, need=${minAnchor + 3}, noteScanLatest=${noteScanLatestHeight})`);
      await new Promise((r) => setTimeout(r, 15_000));
      latestHeight = await this.lwClient.getLatestBlockHeight();
    }
    const anchorHeight = latestHeight - 3;
    // Start scan exactly at birthday (the shield block) so we only find the
    // freshly shielded note, not old already-spent notes from prior transfers
    // whose nullifiers were recorded outside our scan window.
    const startHeight = acct.birthdayHeight
      ? Math.max(1, acct.birthdayHeight)
      : Math.max(1, anchorHeight - 1200);
    const totalBlocks = anchorHeight - startHeight;

    let frontier: string;
    if (startHeight > 1) {
      frontier = await this.lwClient.getOrchardTreeState(startHeight - 1);
      console.log(`[espectro] Exit: fetched tree state at height ${startHeight - 1}, frontier length=${frontier?.length}, prefix=${frontier?.slice(0, 16)}`);
    } else {
      frontier = "00";
    }

    report({ phase: "downloading", startHeight, endHeight: anchorHeight, blocksToScan: totalBlocks });
    const blockData = await this.lwClient.getCompactBlocks(startHeight, anchorHeight);
    const compactBlocksB64 = base64.encode(blockData);

    report({ phase: "scanning", startHeight, endHeight: anchorHeight, blocksToScan: totalBlocks });
    console.log(`[espectro] Exit: scanning ${totalBlocks} blocks (${startHeight}..${anchorHeight}) with tree tracking`);

    const result = localGetShieldedBalance({
      seedB64: acct.seedB64,
      compactBlocks: compactBlocksB64,
      frontier,
    });

    if (!result.anchor) {
      throw new Error("Witness scan did not produce an anchor — tree tracking failed");
    }

    console.log(`[espectro] Exit: witness scan complete — ${result.noteCount} notes, anchor=${result.anchor.slice(0, 16)}...`);
    return result;
  }

  // ---- Recovery -----------------------------------------------------------

  async getTransparentBalance(): Promise<{
    utxos: UTXO[];
    totalZec: number;
  }> {
    const acct = this.requireAddress();
    const utxos = await this.lwClient.getUTXOs(acct.transparentAddress);
    const totalZec = utxos.reduce((sum, u) => sum + parseFloat(u.amount), 0);
    return { utxos, totalZec };
  }

  /**
   * Shield ZEC at the transparent address into the Orchard pool.
   * If there is an active enter operation, uses its depositUtxos for
   * attribution; otherwise shields all UTXOs (with user confirmation).
   */
  async recoverShield(
    onProgress?: (step: string) => void,
  ): Promise<{ txid: string }> {
    const acct = this.requireAccount();
    const report = onProgress ?? (() => {});
    const op = loadOperation();

    report("checking");

    let utxos: UTXO[];
    if (
      op?.type === "enter" &&
      !isTerminalStage(op.stage) &&
      (op.evidence as EnterEvidence).depositUtxos?.length
    ) {
      // Use only the UTXOs attributed to this operation
      utxos = (op.evidence as EnterEvidence).depositUtxos!.map((u) => ({
        txid: u.txid,
        vout: u.vout,
        amount: u.amount,
        script: u.script,
        height: u.height,
      }));
      console.log(
        `[espectro] recoverShield: using ${utxos.length} attributed UTXOs from operation ${op.id}`,
      );
    } else {
      utxos = await this.lwClient.getUTXOs(acct.transparentAddress);
    }

    if (utxos.length === 0) {
      throw new Error("No funds found at transparent address");
    }

    report("shielding");
    const totalZats = utxos.reduce(
      (sum, u) => sum + Math.round(parseFloat(u.amount) * ZATS_PER_ZEC),
      0,
    );
    const fee = zip317Fee(utxos.length, 0);
    const shieldAmountZats = totalZats - fee;
    if (shieldAmountZats <= 0) {
      throw new Error(
        `UTXO value (${totalZats} zats) too small to cover fee (${fee} zats)`,
      );
    }
    const shieldResult = await localCreateShieldingTx({
      seedB64: acct.seedB64,
      utxos: JSON.stringify(utxos),
      amount: shieldAmountZats.toString(),
      provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
    });

    // Update operation if active
    if (op && op.type === "enter" && !isTerminalStage(op.stage)) {
      advanceOperation(op, "shielding_built", {
        shieldRawTx: shieldResult.raw_tx,
        shieldTxId: shieldResult.txid,
        shieldedAmountZats: shieldAmountZats,
      } as Partial<EnterEvidence>);
    }

    if (shieldResult.raw_tx) {
      report("broadcasting");
      const realShieldTxid = await this.lwClient.sendRawTransaction(shieldResult.raw_tx);
      console.log("[espectro] recoverShield tx on-chain:", realShieldTxid);

      if (op && op.type === "enter" && !isTerminalStage(op.stage)) {
        advanceOperation(op, "shielding_broadcasted");
      }
    }

    this.invalidateScanCache();

    report("done");
    return { txid: shieldResult.txid };
  }

  /**
   * Scan the Orchard pool for shielded balance, optionally anchored to a
   * specific shield tx hash or a block window relative to the chain tip.
   */
  async scanOrchardBalance(opts: {
    blocks?: number;
    txHash?: string;
    onProgress?: (p: SyncProgress) => void;
  }): Promise<{ balanceZec: number; noteCount: number }> {
    const acct = this.requireAccount();

    let birthday: number;
    if (opts.txHash) {
      const height = await this.lwClient
        .getAddressShieldBlockHeight(acct.transparentAddress, opts.txHash)
        .catch(() => null);
      if (height == null) {
        throw new Error(
          "Transaction not found on chain. Check the tx hash and try again.",
        );
      }
      birthday = height;
    } else {
      const latest = await this.lwClient.getLatestBlockHeight();
      birthday = Math.max(1, latest - (opts.blocks ?? 3000));
    }

    if (birthday < (acct.birthdayHeight ?? Infinity)) {
      acct.birthdayHeight = birthday;
      saveAccount(acct);
    }

    acct.lastScannedHeight = undefined;
    acct.cachedBalance = undefined;
    acct.cachedNoteCount = undefined;
    acct.cachedNotes = undefined;

    const result = await this.getShieldedBalance(undefined, opts.onProgress);
    return {
      balanceZec: Number(result.balance) / ZATS_PER_ZEC,
      noteCount: result.noteCount,
    };
  }

  /**
   * Unified fund recovery: handles both transparent (T1) funds and shielded
   * Orchard funds, routing through the appropriate path automatically.
   */
  async recoverFunds(params: {
    destinationAsset: string;
    destinationAddress: string;
    onProgress?: (step: string) => void;
  }): Promise<{ txid: string }> {
    const acct = this.requireAccount();
    const report = params.onProgress ?? (() => {});

    report("checking");
    const utxos = await this.lwClient.getUTXOs(acct.transparentAddress);

    if (utxos.length > 0) {
      // --- T1 path ---
      const totalZats = utxos.reduce(
        (s, u) => s + Math.round(parseFloat(u.amount) * ZATS_PER_ZEC),
        0,
      );
      const fee = zip317Fee(utxos.length, 1, 0);
      const sendAmountZats = totalZats - fee;
      if (sendAmountZats <= 0) {
        throw new Error(
          `UTXO value (${totalZats} zats) too small to cover fee (${fee} zats)`,
        );
      }
      const sendZec = (sendAmountZats / ZATS_PER_ZEC).toFixed(8);

      report("quoting");
      const quoteResp = await this.swapClient.quote({
        sellAsset: ZEC_ASSET,
        buyAsset: params.destinationAsset,
        sellAmount: sendZec,
        slippage: 3,
        sourceAddress: acct.transparentAddress,
        destinationAddress: params.destinationAddress,
      });
      if (!quoteResp.routes.length) {
        throw new Error(quoteResp.error ?? "No swap routes available");
      }
      const route = quoteResp.routes[0]!;

      report("signing");
      const swapResp = await this.swapClient.swap({
        routeId: route.routeId,
        sourceAddress: acct.transparentAddress,
        destinationAddress: params.destinationAddress,
        disableBuildTx: true,
        disableBalanceCheck: true,
        disableEstimate: true,
        disableSecurityChecks: true,
      });
      if (swapResp.error) {
        throw new Error(swapResp.error);
      }
      if (!swapResp.inboundAddress) {
        throw new Error("SwapKit did not return an inbound address for ZEC");
      }

      report("building");
      const txResult = localCreateTransparentTx({
        seedB64: acct.seedB64,
        utxos: JSON.stringify(utxos),
        amount: sendAmountZats.toString(),
        destinationAddress: swapResp.inboundAddress,
      });

      report("broadcasting");
      const txid = await this.lwClient.sendRawTransaction(txResult.raw_tx!);

      const op = loadOperation();
      if (op && !isTerminalStage(op.stage)) {
        failOperation(op, "Funds recovered via recovery page");
      }

      report("done");
      return { txid };
    } else {
      // --- Orchard path ---
      report("scanning");
      acct.lastScannedHeight = undefined;
      acct.cachedBalance = undefined;
      acct.cachedNoteCount = undefined;
      acct.cachedNotes = undefined;

      let witnessResult: Awaited<ReturnType<typeof this.prepareExitWitnesses>>;
      try {
        witnessResult = await this.prepareExitWitnesses();
      } finally {
        acct.lastScannedHeight = undefined;
      }

      if (!witnessResult.notes?.length || parseInt(witnessResult.balance) === 0) {
        throw new Error("No shielded balance found in Orchard pool");
      }
      if (!witnessResult.anchor) {
        throw new Error("Witness scan did not produce an anchor");
      }

      const unshieldFee = zip317Fee(0, 1, 2);
      const balanceZats = parseInt(witnessResult.balance);
      const netZats = balanceZats - unshieldFee;
      if (netZats <= 0) {
        throw new Error(
          `Balance (${balanceZats} zats) too small to cover unshield fee (${unshieldFee} zats)`,
        );
      }

      report("building");
      const unshieldResult = await localCreateUnshieldingTx({
        seedB64: acct.seedB64,
        notes: JSON.stringify(witnessResult.notes),
        amount: netZats.toString(),
        destinationTAddr: acct.transparentAddress,
        provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
        anchor: witnessResult.anchor,
        merklePaths: JSON.stringify(
          witnessResult.notes.map((n) => ({
            position: n.position,
            auth_path: n.auth_path,
          })),
        ),
      });

      report("broadcasting_unshield");
      await this.lwClient.sendRawTransaction(unshieldResult.raw_tx!);

      report("confirming");
      const newUtxos = await this.lwClient.pollForDeposit(
        acct.transparentAddress,
        { timeoutMs: 600_000, intervalMs: 15_000 },
      );

      const totalZats2 = newUtxos.reduce(
        (s, u) => s + Math.round(parseFloat(u.amount) * ZATS_PER_ZEC),
        0,
      );
      const fee2 = zip317Fee(newUtxos.length, 1, 0);
      const sendAmountZats2 = totalZats2 - fee2;
      if (sendAmountZats2 <= 0) {
        throw new Error(
          `Unshielded UTXO value (${totalZats2} zats) too small to cover fee (${fee2} zats)`,
        );
      }
      const sendZec2 = (sendAmountZats2 / ZATS_PER_ZEC).toFixed(8);

      report("quoting");
      const quoteResp2 = await this.swapClient.quote({
        sellAsset: ZEC_ASSET,
        buyAsset: params.destinationAsset,
        sellAmount: sendZec2,
        slippage: 3,
        sourceAddress: acct.transparentAddress,
        destinationAddress: params.destinationAddress,
      });
      if (!quoteResp2.routes.length) {
        throw new Error(quoteResp2.error ?? "No swap routes available");
      }
      const route2 = quoteResp2.routes[0]!;

      report("signing");
      const swapResp2 = await this.swapClient.swap({
        routeId: route2.routeId,
        sourceAddress: acct.transparentAddress,
        destinationAddress: params.destinationAddress,
        disableBuildTx: true,
        disableBalanceCheck: true,
        disableEstimate: true,
        disableSecurityChecks: true,
      });
      if (swapResp2.error) {
        throw new Error(swapResp2.error);
      }
      if (!swapResp2.inboundAddress) {
        throw new Error("SwapKit did not return an inbound address for ZEC");
      }

      report("building_tx");
      const txResult2 = localCreateTransparentTx({
        seedB64: acct.seedB64,
        utxos: JSON.stringify(newUtxos),
        amount: sendAmountZats2.toString(),
        destinationAddress: swapResp2.inboundAddress,
      });

      report("broadcasting");
      const txid = await this.lwClient.sendRawTransaction(txResult2.raw_tx!);

      const op = loadOperation();
      if (op && !isTerminalStage(op.stage)) {
        failOperation(op, "Funds recovered via recovery page");
      }

      report("done");
      return { txid };
    }
  }

  /**
   * Return stuck transparent ZEC to the original source via SwapKit.
   *
   * Flow: get UTXOs -> quote ZEC->dest -> swap -> build transparent tx
   *       to SwapKit inbound address -> broadcast -> poll tracking.
   */
  async recoverReturnToOrigin(params: {
    destinationAsset: string;
    destinationAddress: string;
    onProgress?: (step: string) => void;
  }): Promise<{ txid: string; swapId: string }> {
    const acct = this.requireAccount();
    const report = params.onProgress ?? (() => {});
    const op = loadOperation();

    report("checking");

    let utxos: UTXO[];
    if (
      op?.type === "enter" &&
      !isTerminalStage(op.stage) &&
      (op.evidence as EnterEvidence).depositUtxos?.length
    ) {
      utxos = (op.evidence as EnterEvidence).depositUtxos!.map((u) => ({
        txid: u.txid,
        vout: u.vout,
        amount: u.amount,
        script: u.script,
        height: u.height,
      }));
    } else {
      utxos = await this.lwClient.getUTXOs(acct.transparentAddress);
    }

    if (utxos.length === 0) {
      throw new Error("No funds found at transparent address");
    }

    const totalZats = utxos.reduce(
      (sum, u) => sum + Math.round(parseFloat(u.amount) * ZATS_PER_ZEC),
      0,
    );
    // transparent-only tx: no orchard actions
    const fee = zip317Fee(utxos.length, 1, 0);
    const sendAmountZats = totalZats - fee;
    if (sendAmountZats <= 0) {
      throw new Error(
        `UTXO value (${totalZats} zats) too small to cover fee (${fee} zats)`,
      );
    }
    const sendZec = (sendAmountZats / ZATS_PER_ZEC).toFixed(8);

    report("quoting");
    const quoteResp = await this.swapClient.quote({
      sellAsset: ZEC_ASSET,
      buyAsset: params.destinationAsset,
      sellAmount: sendZec,
      slippage: 3,
      sourceAddress: acct.transparentAddress,
      destinationAddress: params.destinationAddress,
    });

    if (!quoteResp.routes.length) {
      throw new Error(quoteResp.error ?? "No swap routes available");
    }

    const route = quoteResp.routes[0]!;

    report("signing");
    const swapResp = await this.swapClient.swap({
      routeId: route.routeId,
      sourceAddress: acct.transparentAddress,
      destinationAddress: params.destinationAddress,
      disableBuildTx: true,
      disableBalanceCheck: true,
      disableEstimate: true,
      disableSecurityChecks: true,
    });

    if (swapResp.error) {
      throw new Error(swapResp.error);
    }

    const inboundAddress = swapResp.inboundAddress;
    if (!inboundAddress) {
      throw new Error("SwapKit did not return an inbound address for ZEC");
    }

    report("building");
    const txResult = localCreateTransparentTx({
      seedB64: acct.seedB64,
      utxos: JSON.stringify(utxos),
      amount: sendAmountZats.toString(),
      destinationAddress: inboundAddress,
    });

    report("broadcasting");
    let broadcastedTxid = txResult.txid;
    if (txResult.raw_tx) {
      broadcastedTxid = await this.lwClient.sendRawTransaction(txResult.raw_tx);
    }

    // Dismiss the current operation if one exists since funds are leaving
    if (op && !isTerminalStage(op.stage)) {
      failOperation(op, "Funds returned to origin via SwapKit");
    }

    report("tracking");
    try {
      const trackResult = await this.swapClient.pollTrack(
        { hash: broadcastedTxid, chainId: "zcash" },
        15_000,
        3_600_000,
      );

      if (!isCompleted(trackResult)) {
        console.warn(
          `[espectro] returnToOrigin: swap terminal but not completed (status=${trackResult.status})`,
        );
      }
    } catch (err) {
      console.warn(
        "[espectro] returnToOrigin: tracking failed/timed out, funds may still arrive:",
        err,
      );
    }

    report("done");
    return { txid: broadcastedTxid, swapId: swapResp.swapId };
  }

  /**
   * Recover shielded Orchard funds by unshielding directly to a ZEC t-address.
   * Use this when funds are stuck in the Orchard pool and no active operation exists.
   */
  async recoverOrchardToAddress(params: {
    destinationTAddr: string;
    onProgress?: (step: string) => void;
  }): Promise<{ txid: string }> {
    const acct = this.requireAccount();
    const report = params.onProgress ?? (() => {});

    report("scanning");
    // Look up all txs that spent from our t-address to find the exact block
    // the shield tx landed in — works even with no localStorage.
    const shieldBlockHeight = await this.lwClient.getAddressShieldBlockHeight(acct.transparentAddress);
    if (!shieldBlockHeight) {
      throw new Error("No shield transaction found for this address. Funds may not be in the Orchard pool.");
    }

    const savedBirthday = acct.birthdayHeight;
    const savedScanned = acct.lastScannedHeight;
    acct.birthdayHeight = shieldBlockHeight;
    acct.lastScannedHeight = undefined;
    acct.cachedBalance = undefined;
    acct.cachedNoteCount = undefined;
    acct.cachedNotes = undefined;
    let witnessResult: Awaited<ReturnType<typeof this.prepareExitWitnesses>>;
    try {
      witnessResult = await this.prepareExitWitnesses();
    } finally {
      acct.birthdayHeight = savedBirthday;
      acct.lastScannedHeight = savedScanned;
    }

    if (parseInt(witnessResult.balance) === 0 || !witnessResult.notes?.length) {
      throw new Error("No shielded balance found in Orchard pool");
    }
    if (!witnessResult.anchor) {
      throw new Error("Witness scan did not produce an anchor");
    }

    const unshieldFee = zip317Fee(0, 1, 2);
    const balanceZats = parseInt(witnessResult.balance);
    const netZats = balanceZats - unshieldFee;
    if (netZats <= 0) {
      throw new Error(`Balance (${balanceZats} zats) too small to cover unshield fee (${unshieldFee} zats)`);
    }

    report("building");
    const unshieldResult = await localCreateUnshieldingTx({
      seedB64: acct.seedB64,
      notes: JSON.stringify(witnessResult.notes),
      amount: netZats.toString(),
      destinationTAddr: params.destinationTAddr,
      provingServiceUrl: import.meta.env.VITE_PROVING_SERVICE_URL,
      anchor: witnessResult.anchor,
      merklePaths: JSON.stringify(
        witnessResult.notes!.map((n) => ({
          position: n.position,
          auth_path: n.auth_path,
        })),
      ),
    });

    report("broadcasting");
    const broadcastedTxid = await this.lwClient.sendRawTransaction(unshieldResult.raw_tx!);
    console.log("[espectro] orchard recovery unshield tx:", broadcastedTxid);

    report("done");
    return { txid: broadcastedTxid };
  }

  /**
   * Retry broadcasting a raw transaction from the current operation.
   */
  async retryBroadcast(rawTx: string): Promise<string> {
    const txHash = await this.lwClient.sendRawTransaction(rawTx);

    // Advance the operation past the broadcast stage
    const op = loadOperation();
    if (op && !isTerminalStage(op.stage)) {
      if (op.type === "enter" && op.stage === "shielding_built") {
        advanceOperation(op, "shielding_broadcasted");
        this.invalidateScanCache();
      } else if (op.type === "exit" && op.stage === "unshield_built") {
        advanceOperation(op, "unshield_broadcasted");
        this.invalidateScanCache();
      }
    }

    return txHash;
  }

  // ---- Swap tracking ------------------------------------------------------

  async trackSwap(hash: string, chainId: string): Promise<TrackResponse> {
    return this.swapClient.track({ hash, chainId });
  }

  // ---- Helpers ------------------------------------------------------------

  private invalidateScanCache(): void {
    const acct = this.requireAccount();
    acct.lastScannedHeight = undefined;
    acct.cachedBalance = undefined;
    acct.cachedNoteCount = undefined;
    acct.cachedNotes = undefined;
    saveAccount(acct);
  }

  private requireAccount(): EspectroAccount {
    if (!this.account) {
      throw new Error(
        "No account loaded. Call createAccount() or loadAccount() first.",
      );
    }
    if (!this.account.seedB64) {
      throw new Error(
        "Wallet signature required. Please reconnect your wallet to continue.",
      );
    }
    return this.account;
  }

  /** For read-only operations that only need the address, not the seed. */
  private requireAddress(): EspectroAccount {
    if (!this.account) {
      throw new Error(
        "No account loaded. Call createAccount() or loadAccount() first.",
      );
    }
    return this.account;
  }
}
